import { useRef, useState, useCallback, useEffect } from 'react';
import type { RichTextEditorRef } from 'mui-tiptap';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';

const useRichTextEditor = (
    initialContent: string,
    editable: boolean,
    onContentChange?: (content: string) => void,
    onSave?: (content: string) => void
) => {
    const rteRef = useRef<RichTextEditorRef>(null);
    const [content, setContent] = useState(initialContent);

    // Mantener la posición del cursor
    const saveCursorPosition = () => {
        const editor = rteRef.current?.editor;
        return editor ? editor.state.selection : null;
    };

    const restoreCursorPosition = (position: any) => {
        const editor = rteRef.current?.editor;
        if (editor && position) {
            editor.commands.setTextSelection(position);
        }
    };

    // Manejar cambios de contenido
    const handleUpdate = useCallback(() => {
        const editor = rteRef.current?.editor;
        if (editor) {
            const updatedContent = editor.getHTML();
            setContent(updatedContent);
            onContentChange?.(updatedContent);
        }
    }, [onContentChange]);

    // Actualizar contenido cuando initialContent cambie
    useEffect(() => {
        const editor = rteRef.current?.editor;
        const cursorPosition = saveCursorPosition();

        if (editor) {
            editor.commands.setContent(initialContent);
            restoreCursorPosition(cursorPosition);
        }
        setContent(initialContent);
    }, [initialContent]);

    // Extensiones necesarias
    const extensions = [
        StarterKit,
        TextStyle,
        Underline,
        Highlight.configure({ multicolor: true }),
        Color,
        TaskList.configure({ HTMLAttributes: { class: 'task-list' } }),
        TaskItem.configure({ HTMLAttributes: { class: 'task-item' } }),
    ];

    // Manejar guardado
    const handleSave = useCallback(() => {
        if (onSave) {
            onSave(content);
        }
    }, [content, onSave]);

    return {
        rteRef,
        content,
        setContent,
        handleUpdate,
        editable,
        extensions,
        handleSave,
    };
};

export default useRichTextEditor;
